/**
 * MobileShell — renders a mobile-first layout (status-aware safe areas,
 * bottom tabs, sync banner) ONLY on small viewports or native platforms.
 * Otherwise returns null so the existing desktop shell takes over.
 */
import { useEffect, useState } from "react";
import { useIsMobile, isNativePlatform } from "./platform";
import { BottomTabNavigator, type MobileTab } from "./BottomTabNavigator";
import { NeuralChat } from "@/components/neural/NeuralChat";
import { AgentsHub } from "@/components/neural/AgentsHub";
import { WorkflowMode } from "@/components/neural/modes/WorkflowMode";
import { useAuth } from "@/hooks/useAuth";
import { useRegisterPush } from "./useRegisterPush";
import { drainQueue, db } from "./db";

interface Props {
  /** When true the shell renders even on desktop (for QA/preview). */
  force?: boolean;
}

export function MobileShell({ force }: Props) {
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [tab, setTab] = useState<MobileTab>("chat");
  const [syncing, setSyncing] = useState(0);

  useRegisterPush(user?.id ?? null);

  // Drain offline queue when network returns
  useEffect(() => {
    let unsub: (() => void) | undefined;
    (async () => {
      try {
        const { Network } = await import("@capacitor/network");
        const handler = await Network.addListener("networkStatusChange", async (s) => {
          if (s.connected) {
            const pending = await db.queue.count();
            setSyncing(pending);
            await drainQueue(async () => {/* dispatch via queryClient */});
            setSyncing(0);
          }
        });
        unsub = () => handler.remove();
      } catch {/* web */}
    })();
    return () => { unsub?.(); };
  }, []);

  if (!force && !isMobile && !isNativePlatform()) return null;

  return (
    <div
      data-testid="mobile-shell"
      className="fixed inset-0 z-30 flex flex-col bg-background text-foreground"
      style={{ paddingTop: "env(safe-area-inset-top)" }}
    >
      {syncing > 0 && (
        <div className="px-4 py-1 text-[11px] text-center bg-primary/10 text-primary border-b border-primary/30">
          Syncing {syncing}…
        </div>
      )}
      <main className="flex-1 overflow-hidden pb-[64px]">
        {tab === "chat"     && <NeuralChat />}
        {tab === "agents"   && <AgentsHub />}
        {tab === "workflow" && <WorkflowMode />}
        {tab === "research" && <NeuralChat />}
        {tab === "profile"  && (
          <div className="p-6 space-y-2">
            <h1 className="text-2xl font-display">Profile</h1>
            <p className="text-sm text-muted-foreground">Signed in as {user?.email ?? "Guest"}</p>
          </div>
        )}
      </main>
      <BottomTabNavigator active={tab} onChange={setTab} />
    </div>
  );
}
